import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, User, LogOut, Crown, Trophy, Heart, Share2, Copy, Check, Facebook, Twitter, MessageCircle, Mail, Link2, Linkedin, X, Trash2, Upload, CheckCircle, XCircle, Clock, Shield, Bell, Phone, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contestOptIn, setContestOptIn] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [gamificationStats, setGamificationStats] = useState(null);
  // ID Verification states
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [idDocType, setIdDocType] = useState('passport');
  const [uploadingId, setUploadingId] = useState(false);
  // WhatsApp Notification states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [notificationPrefs, setNotificationPrefs] = useState({
    whatsapp: true,
    matches: true,
    messages: true,
    votes: true,
    gift_votes: true,
    daily_reminder: false
  });
  const [savingPhone, setSavingPhone] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);

  useEffect(() => {
    loadUser();
    loadGamificationStats();
    loadVerificationStatus();
    loadNotificationSettings();
  }, []);

  const loadGamificationStats = async () => {
    try {
      const response = await axios.get(`${API}/gamification/stats`, { withCredentials: true });
      setGamificationStats(response.data);
    } catch (e) {
      console.error('Failed to load gamification stats:', e);
    }
  };

  const loadVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/verification/status`, { withCredentials: true });
      setVerificationStatus(response.data.status);
    } catch (e) {
      console.error('Failed to load verification status:', e);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/profile/notifications`, { withCredentials: true });
      setPhoneNumber(response.data.phone_number || '');
      setNotificationPrefs(response.data.notification_preferences || {
        whatsapp: true,
        matches: true,
        messages: true,
        votes: true,
        gift_votes: true,
        daily_reminder: false
      });
    } catch (e) {
      console.error('Failed to load notification settings:', e);
    }
  };

  const handleSavePhone = async () => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setSavingPhone(true);
    try {
      await axios.put(`${API}/profile/phone`, {
        phone_number: phoneNumber,
        notification_preferences: notificationPrefs
      }, { withCredentials: true });
      toast.success('Phone number saved successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save phone number');
    } finally {
      setSavingPhone(false);
    }
  };

  const handleUpdateNotificationPrefs = async (key, value) => {
    const newPrefs = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(newPrefs);
    
    try {
      await axios.put(`${API}/profile/notifications`, newPrefs, { withCredentials: true });
      toast.success('Notification preferences updated');
    } catch (e) {
      // Revert on failure
      setNotificationPrefs(notificationPrefs);
      toast.error('Failed to update preferences');
    }
  };

  const handleTestWhatsApp = async () => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter and save your phone number first');
      return;
    }
    
    setTestingWhatsApp(true);
    try {
      const response = await axios.post(`${API}/test-whatsapp`, {}, { withCredentials: true });
      toast.success(response.data.message || 'Test message sent! Check your WhatsApp.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test message');
    } finally {
      setTestingWhatsApp(false);
    }
  };

  const handleIdUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (20MB)
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 20MB.');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPG, PNG, GIF, and PDF allowed.');
      return;
    }

    setUploadingId(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('id_type', idDocType);

    try {
      await axios.post(`${API}/upload/id-document`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('ID document uploaded successfully! Verification pending.');
      setVerificationStatus('pending');
      await loadUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload ID document');
    } finally {
      setUploadingId(false);
    }
  };

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setContestOptIn(response.data.contest_opt_in || false);
      setLoading(false);
    } catch (e) {
      console.error('Failed to load user:', e);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      toast.success('Logged out successfully');
      navigate('/');
    } catch (e) {
      toast.error('Failed to logout');
    }
  };

  const handleUpgradePremium = async () => {
    try {
      await axios.post(`${API}/upgrade-premium`);
      toast.success('Upgraded to Premium!');
      await loadUser();
    } catch (e) {
      toast.error('Failed to upgrade');
    }
  };

  const handleContestToggle = async (checked) => {
    try {
      if (checked) {
        await axios.post(`${API}/contest/opt-in`, {}, { withCredentials: true });
        toast.success('You\'re in the dating contest!');
        setContestOptIn(true);
      } else {
        await axios.post(`${API}/contest/opt-out`, {}, { withCredentials: true });
        toast.info('Opted out of dating contest');
        setContestOptIn(false);
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to update contest status';
      toast.error(errorMsg);
      // Reset toggle if it failed
      setContestOptIn(!checked);
    }
  };

  const handleDeleteProfile = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/profile`, { withCredentials: true });
      toast.success('Your profile has been deleted');
      setShowDeleteConfirm(false);
      // Wait a moment then redirect to landing
      setTimeout(() => {
        navigate('/');
        window.location.reload();
      }, 1500);
    } catch (e) {
      toast.error('Failed to delete profile');
      setDeleting(false);
    }
  };

  const getShareUrl = () => {
    return `${window.location.origin}/profile/${user?.id}`;
  };

  const getShareText = () => {
    return `Vote for ${user?.name} on HeartLink Dating Contest! 💕`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setLinkCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (e) {
      toast.error('Failed to copy link');
    }
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleShareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleShareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(getShareText() + ' ' + getShareUrl())}`;
    window.open(url, '_blank');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(getShareText());
    const body = encodeURIComponent(`Check out my profile and vote for me!\n\n${getShareUrl()}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
          <Button
            data-testid="back-to-discover-btn"
            onClick={() => navigate('/discover')}
            variant="ghost"
            size="icon"
            className="hover:bg-pink-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </Button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
            Settings
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        {/* Profile Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
          <div className="flex items-center space-x-4 mb-4">
            <Avatar className="w-20 h-20">
              <AvatarImage src={user?.photos?.[0]?.startsWith('http') ? user.photos[0] : (user?.photos?.[0] ? `${BACKEND_URL}${user.photos[0]}` : user?.picture)} />
              <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white text-2xl">
                {user?.name?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{user?.name}</h2>
              <p className="text-gray-500">{user?.email}</p>
              {user?.is_premium && (
                <div className="flex items-center space-x-1 mt-1">
                  <Crown className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-yellow-600">Premium Member</span>
                </div>
              )}
            </div>
          </div>
          <Button
            data-testid="edit-profile-btn"
            onClick={() => navigate('/setup')}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full"
          >
            <User className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
          <Button
            data-testid="preview-profile-btn"
            onClick={() => navigate(`/profile/${user?.id}`)}
            variant="outline"
            className="w-full border-purple-300 text-purple-700 hover:bg-purple-50 rounded-full mt-2"
          >
            <User className="w-4 h-4 mr-2" />
            Preview My Profile
          </Button>
        </div>

        {/* Gamification Stats */}
        {gamificationStats && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-2 border-purple-200 mb-6">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Your Referral Stats</h3>
                <p className="text-sm text-gray-600">Share and win amazing prizes!</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border border-purple-200">
                <p className="text-sm text-gray-600">Total Likes</p>
                <p className="text-3xl font-bold text-purple-600">{gamificationStats.likes_count}</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-sm text-gray-600">Referrals</p>
                <p className="text-3xl font-bold text-blue-600">{gamificationStats.referrals_count}</p>
              </div>
              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-xl p-4 border border-pink-200">
                <p className="text-sm text-gray-600">Shares</p>
                <p className="text-3xl font-bold text-pink-600">{gamificationStats.shares_count}</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-200">
                <p className="text-sm text-gray-600">Prizes Won</p>
                <p className="text-3xl font-bold text-orange-600">{gamificationStats.rewards?.length || 0}</p>
              </div>
            </div>

            {/* Referral Link */}
            {gamificationStats.referral_code && (
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-4 border-2 border-purple-300">
                <p className="text-sm font-semibold text-purple-800 mb-1">🔗 Your Referral Link:</p>
                <p className="text-xs text-purple-600 mb-2">Share this link to unlock profile video (5 referrals needed)</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={`https://ios.matrixshopping.com?app=guyloveana&ref=${gamificationStats.referral_code}`}
                    readOnly
                    className="flex-1 bg-white rounded-lg px-3 py-2 text-sm border border-purple-300"
                  />
                  <Button
                    onClick={() => {
                      const link = `https://ios.matrixshopping.com?app=guyloveana&ref=${gamificationStats.referral_code}`;
                      if (navigator.clipboard && window.isSecureContext) {
                        navigator.clipboard.writeText(link).then(() => {
                          toast.success('Referral link copied!');
                        }).catch(() => {
                          // Fallback
                          const textArea = document.createElement('textarea');
                          textArea.value = link;
                          textArea.style.position = 'fixed';
                          textArea.style.left = '-999999px';
                          document.body.appendChild(textArea);
                          textArea.select();
                          try {
                            document.execCommand('copy');
                            toast.success('Referral link copied!');
                          } catch (err) {
                            toast.info(`Copy this link: ${link}`);
                          }
                          document.body.removeChild(textArea);
                        });
                      } else {
                        toast.info(`Copy this link: ${link}`);
                      }
                    }}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-purple-500 mt-2">
                  📊 Progress: {gamificationStats.referrals_count || 0}/5 referrals 
                  {(gamificationStats.referrals_count || 0) >= 5 ? ' ✅ Video Unlocked!' : ''}
                </p>
              </div>
            )}

            {/* Prizes Won */}
            {gamificationStats.rewards && gamificationStats.rewards.length > 0 && (
              <div className="mt-4">
                <h4 className="font-semibold text-gray-800 mb-2">🏆 Prizes Won:</h4>
                <div className="space-y-2">
                  {gamificationStats.rewards.map((reward, idx) => (
                    <div key={idx} className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                      <p className="font-semibold text-yellow-800">{reward.prize_name}</p>
                      <p className="text-xs text-yellow-600">Won with {reward.referrals_count} referrals</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Admin Link */}
            {user?.is_admin && (
              <Button
                onClick={() => navigate('/admin')}
                className="w-full mt-4 bg-gradient-to-r from-red-600 to-pink-600 text-white"
              >
                Admin Dashboard
              </Button>
            )}
          </div>
        )}
        {/* Premium Card */}
        {!user?.is_premium && (
          <div className="bg-gradient-to-br from-yellow-100 to-orange-100 rounded-2xl p-6 shadow-lg border-2 border-yellow-300">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Upgrade to Premium</h3>
                <p className="text-sm text-gray-600">Unlock exclusive features</p>
              </div>
            </div>
            <ul className="space-y-2 mb-4 text-sm text-gray-700">
              <li className="flex items-center space-x-2">
                <Heart className="w-4 h-4 text-pink-500" />
                <span>See who liked you</span>
              </li>
              <li className="flex items-center space-x-2">
                <Heart className="w-4 h-4 text-pink-500" />
                <span>Unlimited swipes</span>
              </li>
              <li className="flex items-center space-x-2">
                <Heart className="w-4 h-4 text-pink-500" />
                <span>Priority in matching</span>
              </li>
            </ul>
            <Button
              data-testid="upgrade-premium-btn"
              onClick={handleUpgradePremium}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white font-bold rounded-full"
            >
              Upgrade Now
            </Button>
          </div>
        )}


        {/* ID Verification Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-2 border-blue-200">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">ID Verification</h3>
              <p className="text-sm text-gray-500">Required for contest participation</p>
            </div>
          </div>

          {/* Verification Status */}
          <div className="mb-4">
            {verificationStatus === 'approved' && (
              <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-300 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-green-800 font-semibold">Verified</p>
                  <p className="text-xs text-green-600">Your identity has been verified</p>
                </div>
              </div>
            )}
            {verificationStatus === 'pending' && (
              <div className="flex items-center space-x-2 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="text-yellow-800 font-semibold">Pending Review</p>
                  <p className="text-xs text-yellow-600">Your ID is being verified by our team</p>
                </div>
              </div>
            )}
            {verificationStatus === 'rejected' && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-300 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="text-red-800 font-semibold">Verification Failed</p>
                  <p className="text-xs text-red-600">Please upload a clearer ID document</p>
                </div>
              </div>
            )}
            {!verificationStatus && (
              <div className="flex items-center space-x-2 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                <Upload className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="text-gray-800 font-semibold">Not Verified</p>
                  <p className="text-xs text-gray-600">Upload your ID to join the contest</p>
                </div>
              </div>
            )}
          </div>

          {/* Upload Section (only if not verified or rejected) */}
          {(!verificationStatus || verificationStatus === 'rejected') && (
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1">ID Document Type</Label>
                <select
                  value={idDocType}
                  onChange={(e) => setIdDocType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="passport">Passport</option>
                  <option value="drivers_license">Driver's License</option>
                  <option value="national_id">National ID</option>
                  <option value="other">Other Government ID</option>
                </select>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">Upload ID Document</Label>
                <p className="text-xs text-gray-500 mb-2">
                  Accepted formats: JPG, PNG, GIF, PDF (max 20MB)
                </p>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
                  onChange={handleIdUpload}
                  disabled={uploadingId}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>

              {uploadingId && (
                <div className="text-center py-2">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-blue-500 border-t-transparent"></div>
                  <p className="text-sm text-gray-600 mt-1">Uploading...</p>
                </div>
              )}

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  🔒 Your ID document is securely stored and only accessible to administrators for verification purposes.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* WhatsApp Notification Settings Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-2 border-green-200">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">WhatsApp Notifications</h3>
              <p className="text-sm text-gray-500">Get notified on WhatsApp</p>
            </div>
          </div>

          {/* Phone Number Input */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Your WhatsApp Number</Label>
              <p className="text-xs text-gray-500 mb-2">Include country code (e.g., +1 for USA, +592 for Guyana)</p>
              <div className="flex space-x-2">
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+592 XXX XXXX"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <Button
                  onClick={handleSavePhone}
                  disabled={savingPhone}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {savingPhone ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>

            {/* Notification Toggles */}
            <div className="space-y-3 pt-2 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700">Send WhatsApp notifications for:</p>
              
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <Heart className="w-5 h-5 text-pink-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">New Matches</p>
                    <p className="text-xs text-gray-500">When someone matches with you</p>
                  </div>
                </div>
                <Switch
                  checked={notificationPrefs.matches}
                  onCheckedChange={(checked) => handleUpdateNotificationPrefs('matches', checked)}
                />
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">New Messages</p>
                    <p className="text-xs text-gray-500">When you receive a new message</p>
                  </div>
                </div>
                <Switch
                  checked={notificationPrefs.messages}
                  onCheckedChange={(checked) => handleUpdateNotificationPrefs('messages', checked)}
                />
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <Heart className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Votes Received</p>
                    <p className="text-xs text-gray-500">When someone votes for you</p>
                  </div>
                </div>
                <Switch
                  checked={notificationPrefs.votes}
                  onCheckedChange={(checked) => handleUpdateNotificationPrefs('votes', checked)}
                />
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Gift Votes Received</p>
                    <p className="text-xs text-gray-500">When someone gifts you votes</p>
                  </div>
                </div>
                <Switch
                  checked={notificationPrefs.gift_votes}
                  onCheckedChange={(checked) => handleUpdateNotificationPrefs('gift_votes', checked)}
                />
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <Bell className="w-5 h-5 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Daily Reminder</p>
                    <p className="text-xs text-gray-500">Daily reminder to check the app</p>
                  </div>
                </div>
                <Switch
                  checked={notificationPrefs.daily_reminder}
                  onCheckedChange={(checked) => handleUpdateNotificationPrefs('daily_reminder', checked)}
                />
              </div>
            </div>

            {/* Test WhatsApp Button */}
            {phoneNumber && (
              <Button
                onClick={handleTestWhatsApp}
                disabled={testingWhatsApp}
                variant="outline"
                className="w-full border-green-500 text-green-700 hover:bg-green-50"
              >
                {testingWhatsApp ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Test WhatsApp Message
                  </>
                )}
              </Button>
            )}

            {/* Info Box */}
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs text-green-800">
                📱 You'll receive WhatsApp messages based on your preferences above. Toggle each notification type on or off as needed!
              </p>
            </div>
          </div>
        </div>

        {/* Contest Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <Label htmlFor="contest-toggle" className="text-lg font-bold text-gray-800 cursor-pointer">
                  Dating Contest
                </Label>
                <p className="text-sm text-gray-500">Get the most votes and win!</p>
              </div>
            </div>
            <Switch
              id="contest-toggle"
              data-testid="contest-toggle"
              checked={contestOptIn}
              onCheckedChange={handleContestToggle}
            />
          </div>
          
          {contestOptIn && (
            <div className="mt-4 space-y-4">
              {/* Prize Description */}
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
                <p className="text-center text-lg font-bold text-purple-800 mb-2">
                  🌍✨ Win an All-Expense Trip Around the World with Your Date! ✨🌍
                </p>
                <p className="text-center text-sm text-purple-600">
                  Get the most votes and experience luxury travel together
                </p>
              </div>

              {/* Activity Images Grid */}
              <div className="grid grid-cols-3 gap-2">
                <div className="relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                  <img 
                    src="https://images.unsplash.com/photo-1633346785060-5222e2ffe3b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjb3VwbGV8ZW58MHx8fHwxNzYxNjE4MTA2fDA&ixlib=rb-4.1.0&q=85&w=400" 
                    alt="Luxury Pool & Yacht"
                    className="w-full h-24 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs font-semibold">Luxury Resorts</p>
                  </div>
                </div>

                <div className="relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                  <img 
                    src="https://images.unsplash.com/photo-1742161627294-2c46bbd9b7e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxyb21hbnRpYyUyMHRyYXZlbHxlbnwwfHx8fDE3NjE2MTgxMTF8MA&ixlib=rb-4.1.0&q=85&w=400" 
                    alt="Romantic Travel"
                    className="w-full h-24 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs font-semibold">World Travel</p>
                  </div>
                </div>

                <div className="relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                  <img 
                    src="https://images.unsplash.com/photo-1630705352366-a037e5cf9a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjb3VwbGV8ZW58MHx8fHwxNzYxNjE4MTA2fDA&ixlib=rb-4.1.0&q=85&w=400" 
                    alt="Luxury Lifestyle"
                    className="w-full h-24 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs font-semibold">Private Jets</p>
                  </div>
                </div>

                <div className="relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                  <img 
                    src="https://images.unsplash.com/photo-1759716550843-23a27a5b8817?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxyb21hbnRpYyUyMHRyYXZlbHxlbnwwfHx8fDE3NjE2MTgxMTF8MA&ixlib=rb-4.1.0&q=85&w=400" 
                    alt="Gondola Romance"
                    className="w-full h-24 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs font-semibold">Yacht Cruises</p>
                  </div>
                </div>

                <div className="relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                  <img 
                    src="https://images.unsplash.com/photo-1647953815782-647b79f68bba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxyb21hbnRpYyUyMHRyYXZlbHxlbnwwfHx8fDE3NjE2MTgxMTF8MA&ixlib=rb-4.1.0&q=85&w=400" 
                    alt="Fine Dining"
                    className="w-full h-24 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs font-semibold">Fine Dining</p>
                  </div>
                </div>

                <div className="relative rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                  <img 
                    src="https://images.pexels.com/photos/34447397/pexels-photo-34447397.jpeg?w=400" 
                    alt="Adventure Activities"
                    className="w-full h-24 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs font-semibold">Adventures</p>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="text-center p-3 bg-gradient-to-r from-pink-100 to-purple-100 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-800 font-semibold">
                  🎉 You're entered! Share your profile to get more votes! 🎉
                </p>
              </div>

              {/* Share Profile Button */}
              <Button
                data-testid="share-profile-btn"
                onClick={() => setShowShareModal(true)}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-full py-6 font-bold"
              >
                <Share2 className="w-5 h-5 mr-2" />
                Share Your Profile to Get More Votes
              </Button>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <Button
          data-testid="logout-btn"
          onClick={handleLogout}
          variant="outline"
          className="w-full border-red-200 text-red-600 hover:bg-red-50 rounded-full py-6"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>

        {/* Delete Profile Button */}
        <Button
          data-testid="delete-profile-btn"
          onClick={() => setShowDeleteConfirm(true)}
          variant="outline"
          className="w-full border-red-500 text-red-700 hover:bg-red-100 hover:border-red-600 rounded-full py-6 mt-4"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Profile Permanently
        </Button>
      </div>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
              Share Your Profile
            </DialogTitle>
            <p className="text-center text-sm text-gray-600 mt-2">
              Get more votes by sharing on social media!
            </p>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Copy Link - Compact Version */}
            <div className="flex items-center gap-2">
              <div className="flex-1 p-2 bg-gray-100 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-800 truncate">{getShareUrl()}</p>
              </div>
              <Button
                onClick={handleCopyLink}
                size="sm"
                className="shrink-0 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
              >
                {linkCopied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Social Media Buttons - Vertical Stack */}
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleShareFacebook}
                variant="outline"
                className="flex items-center justify-center space-x-2 py-3 hover:bg-blue-50 hover:border-blue-400"
              >
                <Facebook className="w-4 h-4 text-blue-600" />
                <span className="text-sm">Facebook</span>
              </Button>

              <Button
                onClick={handleShareWhatsApp}
                variant="outline"
                className="flex items-center justify-center space-x-2 py-3 hover:bg-green-50 hover:border-green-400"
              >
                <MessageCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm">WhatsApp</span>
              </Button>

              <Button
                onClick={handleShareTwitter}
                variant="outline"
                className="flex items-center justify-center space-x-2 py-3 hover:bg-sky-50 hover:border-sky-400"
              >
                <X className="w-4 h-4 text-gray-800" />
                <span className="text-sm">Twitter/X</span>
              </Button>

              <Button
                onClick={handleShareLinkedIn}
                variant="outline"
                className="flex items-center justify-center space-x-2 py-3 hover:bg-blue-50 hover:border-blue-600"
              >
                <Linkedin className="w-4 h-4 text-blue-700" />
                <span className="text-sm">LinkedIn</span>
              </Button>

              <Button
                onClick={handleShareEmail}
                variant="outline"
                className="flex items-center justify-center space-x-2 py-3 hover:bg-gray-50 hover:border-gray-400"
              >
                <Mail className="w-4 h-4 text-gray-600" />
                <span className="text-sm">Email</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Profile Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl font-bold text-red-600">
              ⚠️ Delete Profile?
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-semibold mb-2">
                This action cannot be undone!
              </p>
              <p className="text-xs text-red-700">
                Deleting your profile will permanently remove:
              </p>
              <ul className="text-xs text-red-700 mt-2 space-y-1 list-disc list-inside">
                <li>Your profile information and photos</li>
                <li>All your matches and conversations</li>
                <li>Your posts and comments</li>
                <li>Contest votes and friends</li>
                <li>All your activity on the platform</li>
              </ul>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700">
                Type <span className="text-red-600 font-bold">DELETE</span> to confirm:
              </Label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="w-full mt-2 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none"
                disabled={deleting}
              />
            </div>

            <div className="flex space-x-3">
              <Button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                variant="outline"
                disabled={deleting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteProfile}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Forever
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
